/**
 * Nevada State-Specific Diagnostic Questions
 * All questions reference actual NV regulations, forms, and requirements
 * Regulatory Body: DPBH (Nevada Division of Public and Behavioral Health)
 * 
 * CORRECTED VERSION - December 2025
 * 
 * REGULATORY FRAMEWORK:
 * Nevada home health agencies are regulated under:
 * - NAC 449.749-449.800 (Home Health Agencies section)
 * - NRS Chapter 449 (Medical Facilities and Other Related Entities)
 * - 42 CFR 484 (Medicare Conditions of Participation for certified agencies)
 * 
 * Key NAC Sections:
 * - NAC 449.749 - Definitions
 * - NAC 449.752 - License required
 * - NAC 449.758 - Applicability of license; coverage against certain liabilities
 * - NAC 449.768 - Applicant or licensee to maintain home office in Nevada
 * - NAC 449.782 - Personnel policies
 * - NAC 449.788 - Licensure, registration or certification of certain personnel
 * - NAC 449.793 - Periodic review of patient and clinical records
 * - NAC 449.7935 - Compliance with federal requirements
 * - NAC 449.794 - Clinical records: Maintenance
 * - NAC 449.797 - Clinical records: Contents
 * - NAC 449.800 - Orders of a physician, physician assistant or APRN
 * 
 * Key NRS Sections:
 * - NRS 449.0015 - "Agency to provide nursing in the home" defined
 * - NRS 449.122 - Investigation of applicant for license
 * - NRS 449.123 - Initial and periodic investigations of employees (background checks)
 * - NRS 449.124 - Maintenance of records regarding employee background checks
 * - NRS 449.174 - Disqualifying crimes for employment
 * - NRS 200.5093 - Protection of Older Persons (abuse reporting)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NEVADA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NV-A1',
    itemId: 'A1',
    stateCode: 'NV',
    title: 'DPBH Home Health Agency License Status',
    description: 'Nevada requires all home health agencies to be licensed by DPBH under NAC 449.752',
    severity: 'high',
    regulationCode: 'NAC 449.752',
    regulationName: 'Licensure',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/Health_Facilities_-_Home/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Nevada DPBH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses must be renewed annually. Check your license status with DPBH.',
      },
      {
        id: 'q2',
        question: 'What is your home health agency license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DPBH license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your DPBH Home Health Agency license is current and active per NAC 449.752.',
      atRisk: 'Your home health agency license expires within 90 days. Nevada requires renewal applications to be submitted to DPBH before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Nevada without a valid DPBH license violates NAC 449.752. Contact DPBH immediately at (702) 486-6515.',
      unknown: 'Verify your home health agency license status with DPBH at (702) 486-6515.',
    },
    stateNotes: [
      'Home health agency licenses are valid for 1 year',
      'License required per NAC 449.752 and NRS 449.0015',
      'DPBH conducts unannounced inspections per NAC 449.761',
      'Home office must be maintained in Nevada per NAC 449.768',
    ],
  },

  {
    id: 'NV-A2',
    itemId: 'A2',
    stateCode: 'NV',
    title: 'Home Office Requirement',
    description: 'Nevada requires home health agencies to maintain a home office in Nevada',
    severity: 'high',
    regulationCode: 'NAC 449.768',
    regulationName: 'Home Office',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain a home office located in Nevada?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'NAC 449.768 requires the home office to be in Nevada',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency maintains a home office in Nevada as required by NAC 449.768.',
      atRisk: 'Verify your home office is properly registered with DPBH.',
      nonCompliant: 'Nevada requires home health agencies to maintain a home office in Nevada per NAC 449.768. Contact DPBH at (702) 486-6515.',
      unknown: 'Verify home office requirement with DPBH.',
    },
    stateNotes: [
      'Home office must be located in Nevada per NAC 449.768',
      'Office address must be on file with DPBH',
      'DPBH may conduct inspections at home office',
      'Contact DPBH at (702) 486-6515 for requirements',
    ],
  },

  {
    id: 'NV-A3',
    itemId: 'A3',
    stateCode: 'NV',
    title: 'Medicare Certification Status',
    description: 'Medicare certification requirements for home health agencies in Nevada',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoPs',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/Medicare/Provider-Enrollment-and-Certification/CertificationandComplianc/HHAs',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-covered home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Nevada Medicaid/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency currently Medicare certified?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Yes, fully certified' },
          { value: 'pending', label: 'Certification pending' },
          { value: 'not_certified', label: 'Not Medicare certified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_certified', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current or not required for your services.',
      atRisk: 'Medicare certification is pending. Ensure all Conditions of Participation (42 CFR 484) are met per NAC 449.7935.',
      nonCompliant: 'Providing Medicare services without certification violates federal requirements. Contact DPBH at (702) 486-6515.',
      unknown: 'Verify Medicare certification status if providing Medicare-covered services.',
    },
    stateNotes: [
      'NAC 449.7935 requires compliance with federal Medicare requirements (42 CFR 484)',
      'Medicare certification required to bill Medicare for services',
      'CMS conducts surveys for Medicare-certified agencies',
      'Nevada Medicaid may accept Medicare certification',
    ],
  },

  {
    id: 'NV-A4',
    itemId: 'A4',
    stateCode: 'NV',
    title: 'Nevada Medicaid Provider Enrollment',
    description: 'Nevada Medicaid provider enrollment requirements',
    severity: 'high',
    regulationCode: 'NAC 422',
    regulationName: 'Medicaid',
    agencyName: 'Nevada Division of Health Care Financing and Policy',
    agencyAbbr: 'DHCFP',
    agencyPhone: '(775) 684-3676',
    agencyUrl: 'https://dhcfp.nv.gov/Providers/ProviderEnrollment/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Nevada Medicaid-funded home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as a Nevada Medicaid provider?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes, currently enrolled' },
          { value: 'pending', label: 'Enrollment pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Nevada Medicaid provider enrollment is current.',
      atRisk: 'Medicaid enrollment is pending. Monitor status with DHCFP.',
      nonCompliant: 'Providing Medicaid services without enrollment violates Nevada regulations. Contact DHCFP at (775) 684-3676.',
      unknown: 'Verify Medicaid enrollment status if providing Nevada Medicaid-funded services.',
    },
    stateNotes: [
      'Medicaid enrollment required through Nevada DHCFP per NAC 422',
      'Provider enrollment separate from state licensure',
      'DHCFP Provider Enrollment: (775) 684-3676',
      'Must maintain good standing to continue billing Medicaid',
    ],
  },

  {
    id: 'NV-A5',
    itemId: 'A5',
    stateCode: 'NV',
    title: 'Incident Reporting (NRS 200.5093)',
    description: 'Nevada requires reporting of elder abuse and critical incidents',
    severity: 'high',
    regulationCode: 'NRS 200.5093',
    regulationName: 'Incident Reporting',
    agencyName: 'Nevada Aging and Disability Services Division',
    agencyAbbr: 'ADSD',
    agencyPhone: '(702) 486-3545',
    agencyUrl: 'https://adsd.nv.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written incident reporting policy?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which incidents does your policy require reporting? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'abuse', label: 'Suspected elder/vulnerable adult abuse (to ADSD)' },
          { value: 'injury', label: 'Serious client injury' },
          { value: 'death', label: 'Client death during services' },
          { value: 'missing', label: 'Missing client' },
          { value: 'medication', label: 'Medication errors' },
          { value: 'complaint', label: 'Formal complaints' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Nevada law requires reporting abuse to ADSD: (702) 486-3545',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting policy is comprehensive per NRS 200.5093.',
      atRisk: 'Incident reporting policy may be incomplete. Ensure elder abuse reporting to ADSD is included.',
      nonCompliant: 'No incident reporting policy exists. Failure to report elder abuse violates NRS 200.5093.',
      unknown: 'Review incident reporting requirements under Nevada law.',
    },
    stateNotes: [
      'NRS 200.5093 requires reporting suspected elder/vulnerable adult abuse',
      'Report to Nevada ADSD: (702) 486-3545',
      'Policy should cover: abuse, injury, death, missing client, medication errors',
      'Healthcare workers are mandatory reporters under Nevada law',
    ],
  },

  {
    id: 'NV-A6',
    itemId: 'A6',
    stateCode: 'NV',
    title: 'Liability Coverage',
    description: 'Nevada requires liability coverage for home health agencies',
    severity: 'high',
    regulationCode: 'NAC 449.758',
    regulationName: 'Liability',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gl', label: 'General liability insurance' },
          { value: 'prof', label: 'Professional liability/malpractice insurance' },
          { value: 'wc', label: 'Workers\' compensation insurance' },
          { value: 'auto', label: 'Commercial auto insurance (if transporting clients)' },
          { value: 'bond', label: 'Surety bond' },
        ],
        required: true,
        helpText: 'NAC 449.758 requires coverage against certain liabilities',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets Nevada NAC 449.758 requirements.',
      atRisk: 'Insurance coverage may not fully meet Nevada requirements. Verify minimum coverage levels.',
      nonCompliant: 'Insurance does not meet Nevada requirements per NAC 449.758. Obtain required coverage.',
      unknown: 'Verify insurance meets NAC 449.758 requirements.',
    },
    stateNotes: [
      'NAC 449.758 requires coverage against certain liabilities',
      'General liability, professional liability, and workers compensation recommended',
      'Commercial auto insurance required if transporting clients',
      'Verify specific coverage requirements with DPBH',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES & POLICIES
  // ============================================
  {
    id: 'NV-B1',
    itemId: 'B1',
    stateCode: 'NV',
    title: 'Personnel Policies',
    description: 'Nevada requires written personnel policies for home health agencies',
    severity: 'high',
    regulationCode: 'NAC 449.782',
    regulationName: 'Personnel',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written personnel policies as required by NAC 449.782?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which elements are included in your personnel policies? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'qualifications', label: 'Staff qualifications and job descriptions' },
          { value: 'orientation', label: 'Employee orientation requirements' },
          { value: 'supervision', label: 'Supervision policies' },
          { value: 'evaluation', label: 'Performance evaluation procedures' },
          { value: 'discipline', label: 'Disciplinary procedures' },
          { value: 'records', label: 'Personnel record requirements' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel policies meet NAC 449.782 requirements.',
      atRisk: 'Personnel policies may be incomplete. Review and update per NAC 449.782.',
      nonCompliant: 'No personnel policies exist. This violates NAC 449.782. Develop written policies immediately.',
      unknown: 'Review personnel policy requirements under NAC 449.782.',
    },
    stateNotes: [
      'NAC 449.782 requires written personnel policies',
      'Must include: qualifications, orientation, supervision, evaluation, discipline',
      'Personnel records must be maintained per policy',
      'Policies should be reviewed and updated regularly',
    ],
  },

  {
    id: 'NV-B2',
    itemId: 'B2',
    stateCode: 'NV',
    title: 'Professional License Verification',
    description: 'Nevada requires verification that personnel are properly licensed, registered, or certified',
    severity: 'high',
    regulationCode: 'NAC 449.788',
    regulationName: 'Licenses',
    agencyName: 'Nevada State Board of Nursing',
    agencyAbbr: 'NSBN',
    agencyPhone: '(702) 486-5800',
    agencyUrl: 'https://nevadanursingboard.org/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify that all clinical staff are properly licensed, registered, or certified before providing patient care?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% verified before patient contact' },
          { value: 'most', label: 'Most are verified, some may start before full verification' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: 'NAC 449.788 requires personnel to be properly licensed, registered, or certified',
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and renewals?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All clinical personnel are verified as properly licensed per NAC 449.788.',
      atRisk: 'License verification process needs improvement. Verify all staff before patient contact.',
      nonCompliant: 'No license verification process. This violates NAC 449.788. Implement verification immediately.',
      unknown: 'Verify license verification process against NAC 449.788.',
    },
    stateNotes: [
      'NAC 449.788 requires personnel to be licensed, registered, or certified',
      'Verify before staff provides patient care',
      'Nevada State Board of Nursing: (702) 486-5800',
      'Track license expiration dates and renewals',
      'Primary source verification recommended',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND CHECKS
  // ============================================
  {
    id: 'NV-C1',
    itemId: 'C1',
    stateCode: 'NV',
    title: 'Fingerprint-Based Background Check',
    description: 'Nevada requires fingerprint-based criminal background checks using the NABS system',
    severity: 'high',
    regulationCode: 'NRS 449.122',
    regulationName: 'Background Checks',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/regulatory/hcqc/health-facility-licensing-and-information/background-check-requirements/',
    questions: [
      {
        id: 'q1',
        question: 'Do you use the Nevada Automated Background Check System (NABS) for all employee background checks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'NRS 449.123 requires fingerprint-based background checks through NABS',
      },
      {
        id: 'q2',
        question: 'Which background checks do you complete for all employees? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'state_fingerprint', label: 'Nevada State fingerprint-based check via NABS' },
          { value: 'fbi', label: 'FBI national fingerprint-based check' },
          { value: 'registry', label: 'Registry checks (abuse/neglect registries)' },
          { value: 'nrs_449_174', label: 'Review for NRS 449.174 disqualifying crimes' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do any employees begin working with patients BEFORE their background check is complete?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countLessThan', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check process is complete per NRS 449.122-449.124.',
      atRisk: 'Background check process may be incomplete. Ensure all required checks are performed.',
      nonCompliant: 'Employees working before background check clearance or not using NABS violates NRS 449.122-449.124. Nevada requires NABS checks BEFORE patient contact.',
      unknown: 'Verify background check process against NRS 449.122-449.124 requirements.',
    },
    stateNotes: [
      'Background checks required every 5 years per NRS 449.123',
      'Must use Nevada Automated Background Check System (NABS)',
      'Disqualifying crimes listed in NRS 449.174',
      'Records must be maintained per NRS 449.124',
    ],
  },

  {
    id: 'NV-C2',
    itemId: 'C2',
    stateCode: 'NV',
    title: 'Background Check Record Maintenance',
    description: 'Nevada requires maintenance of background check records',
    severity: 'high',
    regulationCode: 'NRS 449.124',
    regulationName: 'Records',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain the following background check records for each employee? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'fingerprints', label: 'Copy of fingerprints or proof of electronic submission' },
          { value: 'authorization', label: 'Written authorization from employee' },
          { value: 'proof', label: 'Proof fingerprints were submitted to Central Repository' },
          { value: 'results', label: 'Background check results/eligibility determination' },
        ],
        required: true,
        helpText: 'NRS 449.124 specifies required documentation',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check records are complete per NRS 449.124.',
      atRisk: 'Background check records may be incomplete. Review NRS 449.124 requirements.',
      nonCompliant: 'Background check records are missing required elements. This violates NRS 449.124.',
      unknown: 'Verify background check records against NRS 449.124.',
    },
    stateNotes: [
      'NRS 449.124 requires maintenance of background check records',
      'Retain: fingerprint proof, authorization, Central Repository submission, results',
      'Records must be available for DPBH inspection',
      'Background checks valid for 5 years per NRS 449.123',
    ],
  },

  // ============================================
  // SECTION D: CLINICAL RECORDS
  // ============================================
  {
    id: 'NV-D1',
    itemId: 'D1',
    stateCode: 'NV',
    title: 'Clinical Records Maintenance',
    description: 'Nevada requires clinical records to be maintained in accordance with NRS 629.051',
    severity: 'high',
    regulationCode: 'NAC 449.794',
    regulationName: 'Records',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain clinical records in accordance with NRS 629.051?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'NAC 449.794 requires clinical records maintenance per NRS 629.051',
      },
      {
        id: 'q2',
        question: 'How long do you retain patient clinical records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: '5plus' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: '3to4' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records are maintained per NAC 449.794 and NRS 629.051.',
      atRisk: 'Clinical records retention may not meet requirements. Nevada recommends 5+ year retention.',
      nonCompliant: 'Clinical records are not properly maintained. This violates NAC 449.794.',
      unknown: 'Verify clinical records meet NAC 449.794 requirements.',
    },
    stateNotes: [
      'NAC 449.794 requires clinical records maintenance per NRS 629.051',
      'Retain records for at least 5 years (Medicare standard)',
      'Records must be safeguarded and accessible',
      'DPBH may review records during inspections',
    ],
  },

  {
    id: 'NV-D2',
    itemId: 'D2',
    stateCode: 'NV',
    title: 'Clinical Records Contents',
    description: 'Nevada requires specific contents in clinical records',
    severity: 'high',
    regulationCode: 'NAC 449.797',
    regulationName: 'Record Contents',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Which elements do your clinical records contain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'assessment', label: 'Patient assessment' },
          { value: 'plan', label: 'Plan of care/treatment' },
          { value: 'orders', label: 'Physician/PA/APRN orders' },
          { value: 'progress', label: 'Progress notes' },
          { value: 'medications', label: 'Medication records' },
          { value: 'discharge', label: 'Discharge summary' },
        ],
        required: true,
        helpText: 'NAC 449.797 specifies required clinical record contents',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per NAC 449.797.',
      atRisk: 'Clinical records may be missing required elements. Review NAC 449.797.',
      nonCompliant: 'Clinical records are missing multiple required elements. This violates NAC 449.797.',
      unknown: 'Verify clinical records contents against NAC 449.797.',
    },
    stateNotes: [
      'NAC 449.797 specifies required clinical record contents',
      'Must include: assessment, plan of care, orders, progress notes, medications, discharge',
      'Orders must be signed by physician, PA, or APRN per NAC 449.800',
      'Records must be complete, accurate, and timely',
    ],
  },

  {
    id: 'NV-D3',
    itemId: 'D3',
    stateCode: 'NV',
    title: 'Periodic Review of Records',
    description: 'Nevada requires periodic review of patient and clinical records',
    severity: 'medium',
    regulationCode: 'NAC 449.793',
    regulationName: 'Record Review',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency conduct periodic review of patient and clinical records by an appointed committee?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'NAC 449.793 requires periodic review by appointed committee with documented minutes',
      },
      {
        id: 'q2',
        question: 'Are minutes maintained for committee meetings?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Periodic record review meets NAC 449.793 requirements.',
      atRisk: 'Committee minutes should be maintained for record reviews.',
      nonCompliant: 'No periodic record review process. This violates NAC 449.793.',
      unknown: 'Verify record review process against NAC 449.793.',
    },
    stateNotes: [
      'NAC 449.793 requires periodic review by appointed committee',
      'Meeting minutes must be maintained',
      'Review should address care quality and documentation completeness',
      'Include clinical and administrative perspectives',
    ],
  },

  // ============================================
  // SECTION E: PHYSICIAN ORDERS
  // ============================================
  {
    id: 'NV-E1',
    itemId: 'E1',
    stateCode: 'NV',
    title: 'Orders for Patient Care',
    description: 'Nevada requires valid orders from physician, PA, or APRN for patient care',
    severity: 'high',
    regulationCode: 'NAC 449.800',
    regulationName: 'Orders',
    agencyName: 'Nevada Division of Public and Behavioral Health',
    agencyAbbr: 'DPBH',
    agencyPhone: '(702) 486-6515',
    agencyUrl: 'https://dpbh.nv.gov/Reg/HealthFacilities/',
    questions: [
      {
        id: 'q1',
        question: 'Do all patients have valid orders from a physician, PA, or APRN before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start before orders obtained' },
          { value: 'no', label: 'No formal order requirement' },
        ],
        required: true,
        helpText: 'NAC 449.800 requires orders from physician, PA, or APRN',
      },
      {
        id: 'q2',
        question: 'Are orders signed and dated by the prescribing practitioner?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orders process meets NAC 449.800 requirements.',
      atRisk: 'Orders should be obtained before services begin per NAC 449.800.',
      nonCompliant: 'Services without valid orders violates NAC 449.800. Implement proper order process.',
      unknown: 'Verify order process against NAC 449.800.',
    },
    stateNotes: [
      'NAC 449.800 requires orders from physician, PA, or APRN',
      'Orders must be obtained before services begin',
      'Orders must be signed and dated by prescribing practitioner',
      'Include orders in clinical record per NAC 449.797',
    ],
  },

  // ============================================
  // SECTION F: FEDERAL COMPLIANCE
  // ============================================
  {
    id: 'NV-F1',
    itemId: 'F1',
    stateCode: 'NV',
    title: 'Federal Requirements Compliance (Medicare-Certified Agencies)',
    description: 'Medicare-certified agencies must comply with 42 CFR 484',
    severity: 'high',
    regulationCode: 'NAC 449.7935',
    regulationName: 'Federal Compliance',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/Medicare/Provider-Enrollment-and-Certification/CertificationandComplianc/HHAs',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare-certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If Medicare-certified, do you comply with all 42 CFR 484 Conditions of Participation?',
        inputType: 'select',
        options: [
          { value: 'full', label: 'Yes, full compliance' },
          { value: 'working', label: 'Working toward compliance' },
          { value: 'unsure', label: 'Not sure of compliance status' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'NAC 449.7935 requires compliance with federal requirements',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'full', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'working', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Federal compliance requirements are met per NAC 449.7935.',
      atRisk: 'Working toward federal compliance. Ensure all 42 CFR 484 requirements are met.',
      nonCompliant: 'Unclear federal compliance status. Review 42 CFR 484 requirements immediately.',
      unknown: 'Verify compliance with 42 CFR 484 if Medicare-certified.',
    },
    stateNotes: [
      'NAC 449.7935 requires compliance with federal requirements for certified agencies',
      '42 CFR 484 contains Medicare Conditions of Participation',
      'CMS conducts surveys for Medicare-certified agencies',
      'Nevada may accept Medicare certification for state licensure',
    ],
  },

  {
    id: 'NV-F2',
    itemId: 'F2',
    stateCode: 'NV',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to verify employees are not excluded from federal healthcare programs',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG Exclusion List for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly checks' },
          { value: 'hire_only', label: 'Yes, at hire only' },
          { value: 'no', label: 'No checks conducted' },
        ],
        required: true,
        helpText: 'Monthly OIG exclusion checks are recommended for Medicare/Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is conducted monthly.',
      atRisk: 'OIG checks should be conducted monthly, not just at hire.',
      nonCompliant: 'No OIG exclusion checks. This creates significant liability for Medicare/Medicaid billing.',
      unknown: 'Verify OIG exclusion check procedures.',
    },
    stateNotes: [
      'OIG recommends monthly verification for all employees',
      'Check OIG LEIE at exclusions.oig.hhs.gov',
      'Employing excluded individuals can result in civil monetary penalties',
      'OIG Hotline: (800) 447-8477',
    ],
  },

  {
    id: 'NV-F3',
    itemId: 'F3',
    stateCode: 'NV',
    title: 'HIPAA Training',
    description: 'Federal HIPAA training requirements',
    severity: 'high',
    regulationCode: '45 CFR 164.530(b)',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff with access to protected health information (PHI) receive HIPAA training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff during orientation and annually' },
          { value: 'orientation', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal HIPAA training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'orientation', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training meets 45 CFR 164.530(b) requirements.',
      atRisk: 'HIPAA training should be provided annually, not just at orientation.',
      nonCompliant: 'No HIPAA training program. This is a federal violation.',
      unknown: 'Verify HIPAA training program.',
    },
    stateNotes: [
      '45 CFR 164.530(b) requires HIPAA training for staff with PHI access',
      'Training required during orientation and annually',
      'Document all HIPAA training in personnel files',
      'HHS HIPAA Hotline: (800) 368-1019',
    ],
  },

  // ============================================
  // SECTION G: ABUSE REPORTING TRAINING
  // ============================================
  {
    id: 'NV-G1',
    itemId: 'G1',
    stateCode: 'NV',
    title: 'Abuse Reporting Training (NRS 200.5093)',
    description: 'Nevada requires training on mandatory abuse reporting for older persons and vulnerable persons',
    severity: 'high',
    regulationCode: 'NRS 200.5093',
    regulationName: 'Abuse Reporting',
    agencyName: 'Nevada Aging and Disability Services Division',
    agencyAbbr: 'ADSD',
    agencyPhone: '(702) 486-3545',
    agencyUrl: 'https://adsd.nv.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do all employees receive training on mandatory abuse reporting under Nevada law?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees receive abuse reporting training' },
          { value: 'most', label: 'Most employees receive training' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Nevada law requires reporting suspected abuse to ADSD: (702) 486-3545',
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to Nevada ADSD?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff receive abuse reporting training per NRS 200.5093.',
      atRisk: 'Abuse reporting training or knowledge of reporting process is inconsistent.',
      nonCompliant: 'Failure to train staff on mandatory abuse reporting violates Nevada law. Staff who fail to report suspected abuse can face penalties.',
      unknown: 'Verify all staff receive abuse reporting training per NRS 200.5093.',
    },
    stateNotes: [
      'Report suspected abuse to ADSD: (702) 486-3545',
      'Healthcare workers are mandatory reporters under NRS 200.5093',
      'Reports may also be filed online with Nevada Aging and Disability Services',
    ],
  },

  // ============================================
  // SECTION H: EVV (MEDICAID PROVIDERS)
  // ============================================
  {
    id: 'NV-H1',
    itemId: 'H1',
    stateCode: 'NV',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services per 21st Century Cures Act',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'Nevada Division of Health Care Financing and Policy',
    agencyAbbr: 'DHCFP',
    agencyPhone: '(702) 668-4200',
    agencyUrl: 'https://dhcfp.nv.gov/Pgms/EVV/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Nevada Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a DHCFP-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Nevada Medicaid-funded services.',
      atRisk: 'EVV is not being used consistently for all Medicaid visits. DHCFP requires EVV for all personal care services.',
      nonCompliant: 'Nevada Medicaid personal care services require EVV per federal law. Failure to implement EVV can result in claim denials. Contact DHCFP at (702) 668-4200.',
      unknown: 'If you provide Medicaid services, verify EVV compliance with DHCFP requirements.',
    },
    stateNotes: [
      '21st Century Cures Act mandates EVV for Medicaid personal care services',
      'DHCFP administers Nevada EVV requirements',
      'EVV must capture: date, time in/out, location, services, provider identity',
      'DHCFP EVV Contact: (702) 668-4200',
      'Non-compliance may result in claim denials',
    ],
  },
];

export default NEVADA_QUESTIONS;